import React, { useState, useRef, useEffect } from 'react';
import ChatModeIcon from '../icons/ChatModeIcon';
import ResearchModeIcon from '../icons/ResearchModeIcon';
import './ModeSelector.css';

interface ModeOption {
  value: 'chat' | 'research';
  title: string;
  subtitle: string;
  icon: React.FC<{ fill?: string; size?: number; className?: string }>;
}

interface ModeSelectorProps {
  value: boolean; // isAgentMode: true = research, false = chat
  onChange: (value: boolean) => void;
}

const modes: ModeOption[] = [
    {
    value: 'chat',
    title: 'Chat Mode',
    subtitle: 'Ask quick questions.',
    icon: ChatModeIcon
  },
  {
    value: 'research',
    title: 'Research Mode',
    subtitle: 'Research, analyse and generate insights.',
    icon: ResearchModeIcon
  },

];

const ModeSelector: React.FC<ModeSelectorProps> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Convert boolean to mode value
  const currentMode = value ? 'research' : 'chat';
  const selectedOption = modes.find(m => m.value === currentMode) || modes[0];

  // Calculate dropdown position when opened
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (mode: ModeOption) => {
    const newAgentMode = mode.value === 'research';
    console.log('[ModeSelector] Mode selected:', mode.value, '→ isAgentMode:', newAgentMode);
    onChange(newAgentMode);
    setIsOpen(false);
  };

  return (
    <div className="mode-selector" ref={dropdownRef}>
      <button
        ref={triggerRef}
        type="button"
        className="mode-selector-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Select mode"
      >
        <selectedOption.icon
          size={24}
          fill="#6F7680"
        />
      </button>

      {isOpen && (
        <div
          className="mode-selector-dropdown"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`
          }}
        >
          {modes.map((mode) => {
            const isSelected = mode.value === currentMode;
            const IconComponent = mode.icon;

            return (
              <button
                key={mode.value}
                type="button"
                className={`mode-option ${isSelected ? 'mode-option-selected' : ''}`}
                onClick={() => handleSelect(mode)}
              >
                <div className="mode-option-icon">
                  <IconComponent
                    size={24}
                    fill={isSelected ? '#0442BF' : '#6F7680'}
                  />
                </div>
                <div className="mode-option-content">
                  <div className={`mode-option-title ${isSelected ? 'mode-option-title-selected' : ''}`}>
                    {mode.title}
                  </div>
                  <div className="mode-option-subtitle">
                    {mode.subtitle}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ModeSelector;
